import {DownloadIcon, ExternalLinkIcon} from '@heroicons/react/outline';
import type {JSX, ReactNode} from 'react';
import {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Badge, Button, PermissionsProvider, Tabs} from '@/components';
import GenerateOnePageReceiptButton from '@/components/Receipt/GenerateOnePageButton';
import {IPDetailsLink} from '@/components/transactions/Detail';
import BusinessTransactionReceipt from '@/screens/Transaction/Receipt/business';
import {transactionService} from '@/services/transactionService';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {BusinessTransactionChannel} from '@/types/AfriexBusinessTransactionChannel';
import {BusinessTransactionStatus} from '@/types/AfriexBusinessTransactionStatus';
import {statusColors} from '@/types/Transaction';
import {
  getBusinessTxDisplayAmount,
  getBusinessTxnFieldsToDisplay,
  getBusinessTxProcessor,
} from '@/utils/businessTransactionFormatter';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {getErrorMessage} from '@/utils/errorMessage';
import {getReceiptTransactionFileName} from '@/utils/receipt';

import PoolDepositReviewPanel from './PoolDepositReviewPanel';
import TransactionDocumentsPanel from './TransactionDocumentsPanel';

type TransactionDetailProp = {
  data: AfriexBusinessTransaction;
  // Optional "Recent History" tab content, supplied by the details page (it
  // owns the history fetch, export handler and loading state).
  recentHistory?: ReactNode;
};

export default function BusinessTransactionDetail({
  data,
  recentHistory,
}: TransactionDetailProp): JSX.Element {
  // Same amount/currency pair as the list row.
  const {amount: displayAmount, asset: displayAsset} =
    getBusinessTxDisplayAmount(data);

  return (
    <main>
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h2
            className="font-semibold text-lg mr-3"
            data-cy="merchant-transaction-detail-amount"
          >
            {displayAmount} {displayAsset}
          </h2>
          {data.status && (
            <div data-cy="merchant-transaction-detail-header-status">
              <Badge
                label={data.status}
                colorScheme={statusColors[data.status]}
              />
            </div>
          )}
        </div>
        <div className="flex divide-x">
          <span className="text-md text-gray-500 dark:text-gray-300 font-medium">
            {formatDate.DDMonYYYY(data.createdAt?.toString())}
          </span>
        </div>
      </div>

      <div className="bg-white dark:bg-transparent mt-10 w-full">
        <BusinessTransactionDetailTableHeader data={data} />
        <Tabs
          tabs={[
            {
              id: 'details',
              title: 'Details',
              dataCy: 'merchant-transaction-tab-details',
              content: <BusinessTransactionDetailTable data={data} />,
            },
            {
              id: 'documents',
              title: 'Documents',
              dataCy: 'merchant-transaction-tab-documents',
              content: <TransactionDocumentsPanel data={data} />,
            },
            ...(recentHistory
              ? [
                  {
                    id: 'history',
                    title: 'Recent History',
                    dataCy: 'merchant-transaction-tab-history',
                    content: recentHistory,
                  },
                ]
              : []),
          ]}
        />
      </div>

      {/* Keyed so opening another transaction remounts the panel and drops
          its review state, including a dismissed duplicate's block. */}
      <PoolDepositReviewPanel key={data.id} data={data} />
    </main>
  );
}

export function BusinessTransactionDetailTable({
  data,
}: {
  data: AfriexBusinessTransaction;
}): JSX.Element {
  return (
    <div className="w-full">
      {getBusinessTxnFieldsToDisplay(data)?.map((entry, idx) => {
        const key = Object.keys(entry)[0];
        const value = Object.values(entry)[0];

        if (!value || value?.length === 0) {
          return null;
        }

        const idRouteMap: Record<string, (value: string) => string> = {
          'Admin ID': (value) => `/users/${value}`,
          'Customer ID': (value) => `/merchants/users/${value}`,
          'Initiated By': (value) => `/merchants/users/${value}`,
          'Business ID': (value) => `/merchants/businesses/${value}`,
        };

        // Same lookup link as the consumer transaction detail.
        if (key === 'Transaction IP Address') {
          return (
            <div
              key={`container-${idx}`}
              className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0"
              data-cy="merchant-transaction-detail-field"
              data-field-label={key}
            >
              <p className="text-slate-500 text-sm dark:text-slate-100">
                {key}
              </p>
              <p
                className="text-sm font-medium text-gray-700 w-1/2 whitespace-normal overflow-hidden dark:text-gray-100"
                data-cy="merchant-transaction-detail-field-value"
              >
                {value} <IPDetailsLink ipAddress={value as string} />
              </p>
            </div>
          );
        }

        return (
          <Fragment key={`container-${idx}`}>
            {key in idRouteMap ? (
              <div
                key={idx}
                className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0"
                data-cy="merchant-transaction-detail-field"
                data-field-label={key}
              >
                <p className="text-slate-500 text-sm dark:text-slate-100">
                  {key}
                </p>

                <div className="w-1/2">
                  <p className="text-sm font-medium text-gray-700 whitespace-normal overflow-hidden dark:text-gray-100 w-fit">
                    <Link
                      // Only 'Ledger Transaction IDs' produces a string[]; the
                      // keys routed here (idRouteMap's own) are always
                      // strings.
                      to={idRouteMap[key]?.(value as string)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm flex gap-1 text-cyan-700 hover:underline"
                      data-cy="merchant-transaction-detail-field-link"
                    >
                      <span data-cy="merchant-transaction-detail-field-value">
                        {value}
                      </span>
                      <ExternalLinkIcon className="w-4 h-4 self-center" />
                    </Link>
                  </p>
                </div>
              </div>
            ) : (
              <div
                key={idx}
                className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0"
                data-cy="merchant-transaction-detail-field"
                data-field-label={key}
              >
                <p className="text-slate-500 text-sm dark:text-slate-100">
                  {key}
                </p>
                <p
                  className="text-sm font-medium text-gray-700 w-1/2 whitespace-normal overflow-hidden dark:text-gray-100"
                  data-cy="merchant-transaction-detail-field-value"
                >
                  {value}
                </p>
              </div>
            )}
          </Fragment>
        );
      })}
      <div className="hidden">
        <BusinessTransactionReceipt data={data} />
      </div>
    </div>
  );
}

export function BusinessTransactionDetailTableHeader({
  data,
}: TransactionDetailProp) {
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  // SWIFT payouts queued for settlement (settlement 'request') get a
  // settlement advice (invoice) generated server-side. Offer the download
  // once the advice descriptor is on the transaction; the click fetches a
  // short-lived presigned URL from the settlement-advice endpoint.
  const canDownloadInvoice =
    data.destinationPaymentMethod?.channel ===
      BusinessTransactionChannel.SWIFT &&
    data.status === BusinessTransactionStatus.SCHEDULED &&
    !!data.meta?.settlementAdvice;

  // A scheduled settlement-request payout has not settled yet, so a receipt
  // is premature — the settlement-advice invoice (above) covers it.
  const shouldHideReceipt =
    data.status === BusinessTransactionStatus.SCHEDULED &&
    data.meta?.settlement === 'request';

  const downloadInvoice = async () => {
    try {
      setIsDownloadingInvoice(true);
      const advice = await transactionService.getTransactionSettlementAdvice(
        data.id,
      );
      window.open(advice.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to download invoice'));
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  return (
    <>
      <div className="w-full flex justify-between items-center font-semibold">
        <h2 data-cy="merchant-transaction-detail-type">
          {capitalizeWords(data.type?.replace(/_/g, ' '))} Transaction
        </h2>
        <div className="flex gap-4 items-center">
          <div
            className="text-cyan-700 uppercase"
            data-cy="merchant-transaction-detail-processor"
          >
            {getBusinessTxProcessor(data) || 'N/A'}
          </div>
          {canDownloadInvoice && (
            // Gated by the same action as the receipt download — both are
            // merchant-transaction document downloads.
            <PermissionsProvider
              permission={AfriexAdminPermission.TransactionRead}
            >
              <Button
                onClick={downloadInvoice}
                colorScheme="cyan"
                variant="solid"
                leftIcon={<DownloadIcon className="h-5 w-5" />}
                disabled={isDownloadingInvoice}
              >
                {isDownloadingInvoice ? 'Downloading…' : 'Download Invoice'}
              </Button>
            </PermissionsProvider>
          )}
          {!shouldHideReceipt && (
            <GenerateOnePageReceiptButton
              fileName={getReceiptTransactionFileName(data.type)}
              scope={data.id}
              label={`Download Receipt`}
              // Distinct from the hidden receipt copy's own button, which
              // carries the shared `admin-receipt-download` hook.
              dataCy="merchant-transaction-receipt-download"
            />
          )}
        </div>
      </div>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mt-2 mb-5 w-full" />
    </>
  );
}
